import { useEffect, useRef, useState } from 'react'

import { useLazySearchProceduresQuery } from 'features/Schedule/api/medicalIssues.api'
import type { ProcedureSearchRow } from 'features/Schedule/api/medicalIssues.types'

import { PICKER_SEARCH_DEBOUNCE_MS, PROCEDURE_SEARCH_MIN_CHARS } from './issuePicker.utils'

type UseProcedureSearchArgs = {
  term: string
  siteId: string
  enabled: boolean
}

type UseProcedureSearchResult = {
  // [] = none (or below the floor), null = the search failed.
  procedures: ProcedureSearchRow[] | null
  // True from the keystroke until the answer (or failure) lands.
  isSearching: boolean
}

export const useProcedureSearch = ({
  term,
  siteId,
  enabled,
}: UseProcedureSearchArgs): UseProcedureSearchResult => {
  const [searchProcedures] = useLazySearchProceduresQuery()
  const [procedures, setProcedures] = useState<ProcedureSearchRow[] | null>([])
  const [isSearching, setIsSearching] = useState(false)
  const sequenceRef = useRef(0)

  const trimmed = term.trim()
  const shouldSearch = enabled && trimmed.length >= PROCEDURE_SEARCH_MIN_CHARS

  useEffect(() => {
    // A slower earlier keystroke must not overwrite a faster later one.
    const sequence = ++sequenceRef.current
    if (!shouldSearch) {
      setProcedures([])
      setIsSearching(false)
      return undefined
    }

    setIsSearching(true)
    const timer = setTimeout(async () => {
      try {
        const result = await searchProcedures({ q: trimmed, siteId }).unwrap()
        if (sequence !== sequenceRef.current) return
        setProcedures(result.rows)
      } catch {
        if (sequence !== sequenceRef.current) return
        setProcedures(null)
      } finally {
        if (sequence === sequenceRef.current) setIsSearching(false)
      }
    }, PICKER_SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [shouldSearch, trimmed, siteId, searchProcedures])

  return { procedures, isSearching }
}
